import { Avatar, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as RobotIcon } from '../../../assets/icons/chat-icons/robot.svg';
import { ReactComponent as CloseIcon } from '../../../assets/icons/chat-icons/x-circle-fill.svg';
import { ReactComponent as MaximizeIcon } from '../../../assets/icons/chat-icons/caret-up-fill.svg';
import CustomTooltip from '../../../components/tooltip/CustomTooltip';
import useAuth from '../../../utils/hooks/useAuth';
import { useAiChatStore } from '../useAiChatStore';
import { useAiChat } from '../useAiChat';
import { useAiChatMinimizeStore } from '../useAiChatMinimizeStore';
import { primaryDark, secondaryBlue } from '../../../utils/color';
import FloatingChatElement from './FloatingChatElement';
import { ChatInput } from '../ChatInput';
import AiChatDisclaimer from '../AiChatDisclaimer';

interface FloatingAiAssistantProps {
  onClose: () => void;
}

/**
 * Floating AI Assistant Chat Widget
 * Displays the active AI conversation in a floating window
 */
export default ({ onClose }: FloatingAiAssistantProps) => {
  const {
    authData: { userData },
  } = useAuth();

  const navigate = useNavigate();

  const { conversations, activeConversationId, isTyping } = useAiChatStore();

  const { sendMessage: sendAiMessage } = useAiChat();
  const { setMinimized } = useAiChatMinimizeStore();

  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const activeConversation = conversations.find((c) => c.id === activeConversationId);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [activeConversation?.messages]);

  const sendMessage = async (content: string) => {
    if (!activeConversation) return;
    await sendAiMessage(activeConversation.id, content);
  };

  const handleMaximize = () => {
    setMinimized(false);
    navigate('/dashboard/chat?showAiAssistant=true');
  };

  if (!activeConversation) {
    return null;
  }

  return (
    <FloatingChatElement position="relative" width="410px" height="640px">
      <Stack height="100%" direction="column">
        {/* Header */}
        <Stack
          px={2}
          py={1.5}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="secondaryBlue.100"
          sx={{ backgroundColor: '#fff', flexShrink: 0 }}
        >
          <Stack direction="row" gap={1} alignItems="center">
            <Avatar
              sx={{
                width: 32,
                height: 32,
                backgroundColor: 'secondaryBlue.500',
              }}
            >
              <RobotIcon width={18} height={18} fill="#fff" />
            </Avatar>
            <Stack>
              <Typography variant="heading5" color="primaryDark.600" fontSize="14px">
                {activeConversation.title}
              </Typography>
              <Typography variant="body3" color="primaryDark.400" fontSize="11px">
                AI Assistant
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" gap={0.5}>
            <CustomTooltip title="Maximize" placement="bottom">
              <IconButton
                sx={{
                  p: 0.5,
                  backgroundColor: 'secondaryBlue.50',
                  '&:hover': {
                    backgroundColor: 'secondaryBlue.100',
                  },
                }}
                onClick={handleMaximize}
              >
                <MaximizeIcon fill={secondaryBlue[500]} width={20} height={20} />
              </IconButton>
            </CustomTooltip>
            <IconButton sx={{ p: 0.5 }} onClick={onClose}>
              <CloseIcon fill={primaryDark[500]} width={20} height={20} />
            </IconButton>
          </Stack>
        </Stack>
        <AiChatDisclaimer fontSize="11px" />

        {/* Messages Area */}
        <Stack
          ref={messagesContainerRef}
          flex={1}
          px={2}
          py={2}
          gap={1.5}
          sx={{
            overflowY: 'auto',
            minHeight: 0,
            overscrollBehavior: 'contain',
            backgroundColor: '#FAFAFA',
          }}
        >
          {activeConversation.messages.length === 0 ? (
            <Stack py={4} alignItems="center" gap={1.5}>
              <RobotIcon width={40} height={40} fill="#6D51F6" />
              <Typography
                variant="heading5"
                color="primaryDark.600"
                textAlign="center"
                fontSize="14px"
              >
                How can I help you?
              </Typography>
              <Typography
                variant="body3"
                color="primaryDark.400"
                textAlign="center"
                fontSize="12px"
              >
                Ask me anything about your projects or Spona.
              </Typography>
              {/* <AiChatDisclaimer /> */}
            </Stack>
          ) : (
            <>
              {activeConversation.messages.map((message) => (
                <Stack
                  key={message.id}
                  direction="row"
                  gap={1}
                  alignItems="flex-start"
                  justifyContent={message.role === 'user' ? 'flex-end' : 'flex-start'}
                >
                  {message.role === 'assistant' && (
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        backgroundColor: 'secondaryBlue.500',
                      }}
                    >
                      <RobotIcon width={14} height={14} fill="#fff" />
                    </Avatar>
                  )}
                  <Stack
                    sx={{
                      maxWidth: '75%',
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: message.role === 'user' ? 'secondaryBlue.500' : '#fff',
                    }}
                  >
                    <Typography
                      variant="body3"
                      fontSize="13px"
                      color={message.role === 'user' ? '#fff' : 'primaryDark.600'}
                      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {message.content}
                    </Typography>
                  </Stack>
                  {message.role === 'user' && (
                    <Avatar
                      src={userData?.avatar}
                      sx={{
                        width: 24,
                        height: 24,
                      }}
                    >
                      {userData?.firstName?.[0]}
                    </Avatar>
                  )}
                </Stack>
              ))}
              {isTyping && (
                <Stack direction="row" gap={1} alignItems="flex-start">
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: 'secondaryBlue.500',
                    }}
                  >
                    <RobotIcon width={14} height={14} fill="#fff" />
                  </Avatar>
                  <Stack
                    sx={{
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: '#fff',
                    }}
                  >
                    <CircularProgress size={14} />
                  </Stack>
                </Stack>
              )}
            </>
          )}
        </Stack>

        {/* Input Area */}
        <Stack
          px={2}
          py={2}
          borderTop="1px solid"
          borderColor="secondaryBlue.100"
          sx={{ backgroundColor: '#fff', flexShrink: 0 }}
        >
          <ChatInput
            onSendMessage={sendMessage}
            disabled={isTyping}
            maxRows={3}
            buttonHeight="40px"
            buttonMinWidth="60px"
            gap={1}
            fontSize="13px"
          />
        </Stack>
      </Stack>
    </FloatingChatElement>
  );
};
